import React, { useEffect, useState } from "react";
import { axiosPost } from "../../../utils/AxiosApi";
import { URL } from "../../../utils/Constants";
import swal from "sweetalert";
import { useARRCode } from "../context/ARRLevelContext";

export const useAssessmentType = () => {
  const [assessmentType, setAssessmentType] = useState([]);

  const getAssessmentType = () => {
    axiosPost(URL.selectAssessmentScales, {}, (response) => {
      if (response.status === 200) {
        const data = response?.data?.data;
        setAssessmentType(data);
      }
    });
  };

  useEffect(() => {
    getAssessmentType();
  }, []);

  return { assessmentType };
};

export const useGPA = () => {
  const [gpa, setGpa] = useState([]);
  const [loading, setLoading] = useState(false);

  const getGPA = () => {
    setLoading(true);
    axiosPost(
      URL.gpaSelect,
      {},
      (response) => {
        if (response.status === 200) {
          const data = response.data.data;
          setGpa(data);
        }
        setLoading(false);
      },
      (error) => {
        swal(
          "Oops",
          error?.response?.data?.message || "Failed to get default GPA values",
        );
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    getGPA();
  }, []);

  return { gpa, setGpa, loading, refetchGPA: getGPA };
};

export const useAssessmentScale = (payload, pageSize) => {
  const { arrLevel } = useARRCode();
  const [assessmentScale, setAssessmentScale] = useState([]);
  const [totalRecordsCount, setTotalRecordsCount] = useState(0);
  const [loading, setLoading] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  const createAssessmentScale = (data) => {
    if (!data) return;
    setLoading((prev) => ({ ...prev, create: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.insertAssessmentScaleLevel,
        data,
        (response) => {
          if (response.status === 200) {
            swal("Success", "Assessment scale created successfully");
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, create: false }));
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to create assessment scale",
          );
          setLoading((prev) => ({ ...prev, create: false }));
          reject(error);
        },
      );
    });
  };

  //assessment scale type - level with image
  const createAssessmentScaleWithImage = (data) => {
    if (!data) return;
    setLoading((prev) => ({ ...prev, create: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.insertAssessmentScaleLevelImage,
        data,
        (response) => {
          if (response.status === 200) {
            swal("Success", "Assessment scale created successfully");
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, create: false }));
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to create assessment scale",
          );
          setLoading((prev) => ({ ...prev, create: false }));
          reject(error);
        },
      );
    });
  };

  const updateAssessmentScaleWithImage = (data) => {
    if (!data) return;
    setLoading((prev) => ({ ...prev, update: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.updateAssessmentLevelWithImage,
        data,
        (response) => {
          if (response.status === 200) {
            swal("Success", "Assessment scale updated successfully");
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, update: false }));
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to update assessment scale",
          );
          setLoading((prev) => ({ ...prev, update: false }));
          reject(error);
        },
      );
    });
  };

  const updateAssessmentScale = (data) => {
    if (!data) return;
    setLoading((prev) => ({ ...prev, update: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.updateAssessmentScale,
        data,
        (response) => {
          if (response.status === 200) {
            swal("Success", "Assessment scale updated successfully");
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, update: false }));
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to update assessment scale",
          );
          setLoading((prev) => ({ ...prev, update: false }));
          reject(error);
        },
      );
    });
  };

  const getAssessmentScale = (page = 1) => {
    setLoading((prev) => ({ ...prev, read: true }));
    axiosPost(
      URL.selectAssessmentScale,
      {
        assessmentModuleCode: arrLevel,
        offset: page - 1,
        limit: pageSize,
        ...payload,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setAssessmentScale(data);
          setTotalRecordsCount(response?.data?.totalRecordsCount);
        }
        setLoading((prev) => ({ ...prev, read: false }));
      },
      (error) => {
        swal(
          "Oops",
          error?.response?.data?.message || "Failed to get assessment scale",
        );
        setLoading((prev) => ({ ...prev, read: false }));
      },
    );
  };

  const deleteAssessmentScale = (id) => {
    setLoading((prev) => ({ ...prev, delete: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.deleteAssessmentScale,
        { id: id },
        (response) => {
          if (response.status === 200) {
            swal(
              "Success",
              response?.message || "Assessment Scale Deleted Successfully",
            );
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, delete: false }));
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to delete assessment scale",
          );
          setLoading((prev) => ({ ...prev, delete: false }));
          reject(error);
        },
      );
    });
  };

  useEffect(() => {
    getAssessmentScale();
  }, [arrLevel]);

  return {
    assessmentScale,
    totalRecordsCount,
    loading,
    refetch: getAssessmentScale,
    deleteAssessmentScale,
    createAssessmentScale,
    createAssessmentScaleWithImage,
    updateAssessmentScaleWithImage,
    updateAssessmentScale,
  };
};

export const useAssociateAssessmentScale = () => {
  const { arrLevel } = useARRCode();
  const [associateAssessmentLevel, setAssociateAssessementLevel] = useState([]);

  const getAssociateAssessmentLevel = (data) => {
    if (!data.assessmentScaleTypeCode || !data.assessmentModuleCode) return;
    axiosPost(URL.selectAssessmentScale, data, (response) => {
      if (response.status === 200) {
        const data = response?.data?.data;
        setAssociateAssessementLevel(data);
      }
    });
  };

  return { associateAssessmentLevel, getAssociateAssessmentLevel };
};

export const useSelectAssessmentScaleLevel = () => {
  const [assessmentScaleLevel, setAssessmentScaleLevel] = useState([]);

  const selectAssessmentScaleLevel = (id) => {
    if (!id) return;
    axiosPost(URL.selectAssessmentScaleLevel, { id: id }, (response) => {
      if (response.status === 200) {
        const data = response?.data?.data;
        setAssessmentScaleLevel(data);
      }
    });
  };

  return { assessmentScaleLevel, selectAssessmentScaleLevel };
};
